<script lang="ts" module>
	declare global {
		interface Window {
			grecaptcha: {
				execute(): Promise<void>;
				reset(): void;
				getResponse(): string;
				ready(cb: () => void): void;
			};
		}
	}
</script>

<script lang="ts">
	import type { Snippet } from "svelte";
	import type { HTMLFormAttributes } from "svelte/elements";
	import type { MaybePromise } from "@core/utils/MaybePromise.js";
	import { getGlobalSettings } from "../../contexts/getGlobalSettings.js";
	import { handleInvalidInputs } from "./handleInvalidInputs.js";

	let {
		form = $bindable(),
		/* eslint-disable prefer-const */
		children,
		onsubmit,
		...rest
		/* eslint-enable prefer-const */
	}: Omit<HTMLFormAttributes, "onsubmit"> & {
		form?: HTMLFormElement;
		children: Snippet;
		onsubmit?(this: void, event: SubmitEvent, form: HTMLFormElement): MaybePromise<void>;
	} = $props();

	const { RECAPTCHA_SITE_KEY } = getGlobalSettings();

	let ready = false;
	async function waitForReadyRecatpcha(): Promise<void> {
		if (ready) {
			return;
		}
		await new Promise((resolve) => {
			setInterval(() => {
				if (ready) {
					resolve(undefined);
				}
			}, 500);
		});
	}

	async function handleAntispamSubmit(
		event: SubmitEvent & { currentTarget: EventTarget & HTMLFormElement },
	): Promise<void> {
		const { currentTarget } = event;
		event.preventDefault();

		await waitForReadyRecatpcha();
		await window.grecaptcha.execute();
		const token = window.grecaptcha.getResponse();
		if (token) {
			window.grecaptcha.reset();
			await onsubmit?.(event, currentTarget);
		}
	}

	function recaptcha(_element: HTMLElement): void {
		window.grecaptcha.reset();
		window.grecaptcha.ready(() => {
			ready = true;
		});
	}

	/* eslint-disable svelte/no-at-html-tags */
</script>

<svelte:head>
	{@html `<script src="https://www.google.com/recaptcha/api.js" async defer></script>`}
</svelte:head>

<form bind:this={form} use:handleInvalidInputs {...rest} onsubmit={handleAntispamSubmit}>
	<div class="g-recaptcha z-[10000]" data-sitekey={RECAPTCHA_SITE_KEY} data-size="invisible" use:recaptcha></div>

	{@render children()}
</form>
